import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import useApi from "../../services/useApi";
import EmptyListingScreen from "../emptyScreens/EmptyListingScreen";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import { setBalanceSheetData, resetBalanceSheetState } from "../../store/slices/balanceSheetSlice";
import DynamicFilterPanel, { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import ButtonWithImg from "../buttons/ButtonWithImg";
import { showToast } from "../toastMessage/toastSlice";
import BalanceSheetShimmer from "./BalanceSheetShimmer";

interface MainTableProps {
    staticText: {
        emptyScreenText: {
            heading1: string;
            heading2: string;
        };
    };
    mainEndPoint: string;
    TableDetails: any;
    handleActions: (id: string, key: string, text?: string) => void;
    params?: { [key: string]: string };
    searchPlaceholder?: string;
    filters: boolean;
    exporting: boolean;
    serviceNow?: boolean;
    displayTotalRecords?: boolean;
    noListing?: boolean;
    notfoundRoute?: { [key: string]: { [key: string]: string } };
    title?: string;
    placeholderClassName?: string;
    dynamicFilterConfig?: DynamicFilterConfig;
    renderTopBarExtras?: any;
    renderSummary?: any;
    heightClassName?: string;
    hasActivateDeactivateMDPrivilege?: boolean;
    hasViewMDPrivilege?: boolean;
    hasEditMDPrivilege?: boolean;
    addMorePadding?: boolean;
    customHeight?: string;
}

// Helper function to format AFA amounts
function formatAfaAmount(amount: any) {
    const num = Number(amount || "0.00");
    if (isNaN(num)) return "AFA 0.00";
    if (num < 0) {
        return `AFA (-) ${Math.abs(num).toFixed(2)}`;
    }
    return `AFA ${num.toFixed(2)}`;
}

export default function BalanceSheet({
    mainEndPoint,
    params = {},
    filters,
    exporting,
    dynamicFilterConfig,
    addMorePadding,
    customHeight
}: MainTableProps) {
    const dispatch = useDispatch<AppDispatch>();
    const { loading, error, data: apiData } = useSelector((state: RootState) => state.balanceSheet);
    const { translate, notEnglish } = useTranslation(breadcrumbsContent);
    const filersWithEmpty = {
        heading1: translate("No results found"),
        heading2: translate("Try adjusting your filter's or search criteria"),
        img: "search_empty_screen_img",
    };
    const [searchParams, setSearchParams] = useSearchParams(params);
    const { getAPI } = useApi();
    const [isExportOpen, setIsExportOpen] = useState(false);
    const exportRef = useRef<HTMLDivElement>(null);
    const [exportLoading, setExportLoading] = useState(false);

    // Filter logic
    const handleFilter = (filters: { [key: string]: any } | null) => {
        setSearchParams((prev) => {
            const updatedParams = new URLSearchParams(prev);
            // updatedParams.set("page_number", "1");
            if (filters === null && dynamicFilterConfig) {
                Object.values(dynamicFilterConfig).forEach((conf: any) => {
                    if (conf.type === "date" && typeof conf.searchKey === "object") {
                        updatedParams.delete(conf.searchKey.start);
                        updatedParams.delete(conf.searchKey.end);
                    } else if (typeof conf.searchKey === "string") {
                        updatedParams.delete(conf.searchKey);
                    }
                });
                return updatedParams;
            }
            if (filters) {
                Object.keys(filters).forEach((key) => {
                    let value = filters[key];
                    if (
                        value &&
                        typeof value === "object" &&
                        ("start" in value || "end" in value)
                    ) {
                        const { start, end } = value as { start?: any; end?: any };
                        if (start && typeof start.format === "function") {
                            updatedParams.set(`${key}_start`, start.format("YYYY-MM-DD"));
                        } else {
                            updatedParams.delete(`${key}_start`);
                        }
                        if (end && typeof end.format === "function") {
                            updatedParams.set(`${key}_end`, end.format("YYYY-MM-DD"));
                        } else {
                            updatedParams.delete(`${key}_end`);
                        }
                        updatedParams.delete(key);
                    }
                    else if (value && typeof value === "object" && typeof value.format === "function") {
                        value = value.format("YYYY-MM-DD");
                        updatedParams.set(key, value.toString());
                    }
                    else if (value !== null && value !== "") {
                        updatedParams.set(key, value.toString());
                    } else {
                        updatedParams.delete(key);
                    }
                });
            }
            return updatedParams;
        });
    };

    // Export logic
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                exportRef.current &&
                !exportRef.current.contains(event.target as Node)
            ) {
                setIsExportOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Extract data for rendering
    const balanceData = apiData || {};

    const exportOptions = [
        { label: "CSV", value: "csv", textColorClass: "text-positive" },
        ...(balanceData?.assets?.details && balanceData?.assets.details.length < 3000
            ? [{ label: "PDF", value: "pdf", textColorClass: "text-s_dark_hover" }]
            : []),
    ];

    const handleExportOptionClick = async (value: string): Promise<void> => {
        setIsExportOpen(false);
        try {
            setExportLoading(true);
            let res;
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const paramString = new URLSearchParams(Object.fromEntries(searchParams.entries())).toString();
            const endpoint = `${mainEndPoint}/export?${paramString}`;
            res = await getAPI(`${endpoint}&export_format=${value}&client_timezone=${timezone}`);
            if (!res.error) {
                if (res.data.url) {
                    window.open(res.data.url, "_blank");
                    dispatch(
                        showToast({
                            type: "success",
                            message: translate("List exported successfully"),
                        })
                    );
                }
            } else {
                dispatch(
                    showToast({
                        type: "error",
                        message: "something went wrong",
                    })
                );
            }
        } catch (error) {
            dispatch(
                showToast({
                    type: "error",
                    message: "something went wrong",
                })
            );
        } finally {
            setExportLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            dispatch(resetBalanceSheetState());
            // Remove page_number from params before making API call
            const paramsObj = Object.fromEntries(searchParams.entries());
            delete paramsObj.page_number;
            const paramString = new URLSearchParams(paramsObj).toString();
            const endpoint = `${mainEndPoint}${paramString ? `?${paramString}` : ""}`;
            const response = await getAPI(endpoint);
            dispatch(setBalanceSheetData(response));
        };
        fetchData();
        // eslint-disable-next-line
    }, [searchParams, mainEndPoint]);

    // Layout constants
    const sectionClass = "bg-p_light text-heading5-semi text-[#4B5563] py-2";
    const cellClass = "px-4 py-2 text-body2 text-[#1f1f1f]";
    const totalClass =
        "px-4 py-2 text-large text-p_primary flex justify-between";
    const colHeaderClass = "text-title1 text-black  px-4 py-2";

    return (
      <div
        className={`relative h-maintableheight2 mb-4 ${
          addMorePadding ? "mr-[34px] ml-3" : "mx-3"
        } mt-3 bg-white rounded-lg`}
      >
        {loading ? (
          <BalanceSheetShimmer />
        ) : (
          <>
            <div className="sticky top-0 z-20 bg-white rounded-t-lg flex justify-between items-center pt-4 pb-1 pr-6 pl-4">
              <h1 className="text-heading4-semi">
                Balance Sheet as of{" "}
                {new Date().toLocaleDateString().replace(/\//g, "-")}
              </h1>
              <div className="flex gap-x-3 items-center justify-center">
                {filters && dynamicFilterConfig && (
                  <DynamicFilterPanel
                    config={dynamicFilterConfig}
                    onApply={handleFilter}
                    onClear={() => handleFilter(null)}
                    initialValues={{}}
                    className=""
                    customHeight={customHeight}
                  />
                )}
                <div className="relative" ref={exportRef}>
                  {exporting && (
                    <ButtonWithImg
                      buttonName={translate("Export")}
                      disabledButton={
                        loading ||
                        !balanceData.assets ||
                        !balanceData.assets.details ||
                        balanceData.assets.details.length === 0
                      }
                      ImgSrc={
                        loading ||
                        !balanceData.assets ||
                        !balanceData.assets.details ||
                        balanceData.assets.details.length === 0
                          ? "loading-export"
                          : "export"
                      }
                      testId="export"
                      className={`px-4 bg-p_primary text-white ${
                        notEnglish ? "w-fit" : "w-[110px]"
                      }`}
                      buttonOnclick={() => setIsExportOpen(!isExportOpen)}
                      loading={exportLoading}
                    />
                  )}
                  {isExportOpen && (
                    <div className="absolute right-0 mt-1 w-[110px] bg-white border border-s_dark_hover rounded-lg z-[11]">
                      <ul className="flex flex-col">
                        {exportOptions.map((option) => (
                          <li
                            key={option.value}
                            data-testid={`${option.value}`}
                            className={`p-2 text-body2 cursor-pointer ${option.textColorClass} hover:bg-p_light rounded-lg`}
                            onClick={() => handleExportOptionClick(option.value)}
                          >
                            {option.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="overflow-y-auto scrollBar px-4 pt-6 h-balanceSheetHeight">
              <div className="grid grid-cols-2 gap-8">
                {/* Assets */}
                <div>
                  {/* <div >Assets</div> */}
                  <div className="bg-white">
                    <div className={`${sectionClass} flex justify-between`}>
                      <div className={colHeaderClass}>Assets</div>
                      <div className={colHeaderClass}>Amount</div>
                    </div>
                    {balanceData?.assets?.details?.map((item: any, idx: number) => (
                      <div className="flex justify-between py-1" key={idx}>
                        <div className={cellClass}>{item.name}</div>
                        <div className={cellClass}>
                          {formatAfaAmount(item.balance)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Liabilities */}
                <div>
                  <div className="bg-white">
                    <div className={`${sectionClass} flex justify-between`}>
                      <div className={colHeaderClass}>Liabilities</div>
                      <div className={colHeaderClass}>Amount</div>
                    </div>
                    {balanceData.liabilities?.details?.map(
                      (item: any, idx: number) => (
                        <div className="flex justify-between py-1" key={idx}>
                          <div className={cellClass}>{item.name}</div>
                          <div className={cellClass}>
                            {formatAfaAmount(item.balance)}
                          </div>
                        </div>
                      )
                    )}
                    <div>
                      <div className={totalClass}>
                        <span>Total Liability</span>
                        <span>
                          {formatAfaAmount(balanceData.liabilities?.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="bg-white mt-1">
                      <div className={`${sectionClass} flex justify-between`}>
                        <div className={colHeaderClass}>Equity/Capital</div>
                        <div className={colHeaderClass}>Amount</div>
                      </div>
                      {balanceData.equity_capital?.details?.map(
                        (item: any, idx: number) => (
                          <div className="flex justify-between py-1" key={idx}>
                            <div className={cellClass}>{item.name}</div>
                            <div className={cellClass}>
                              {formatAfaAmount(item.balance)}
                            </div>
                          </div>
                        )
                      )}
                      <div>
                        <div className={totalClass}>
                          <span>Total Equity</span>
                          <span>
                            {formatAfaAmount(balanceData.equity_capital?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div>
                      <div className={`${sectionClass} flex justify-between`}>
                        <div className={colHeaderClass}>Revenue</div>
                        <div className={colHeaderClass}>Amount</div>
                      </div>
                      {balanceData.revenue?.details?.map(
                        (item: any, idx: number) => (
                          <div className="flex justify-between py-1" key={idx}>
                            <div className={cellClass}>{item.name}</div>
                            <div className={cellClass}>
                              {formatAfaAmount(item.balance)}
                            </div>
                          </div>
                        )
                      )}
                      <div className={totalClass}>
                        <span>Total Revenue</span>
                        <span>
                          {formatAfaAmount(balanceData.revenue?.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div>
                      <div className={`${sectionClass} flex justify-between`}>
                        <div className={colHeaderClass}>Expenses</div>
                        <div className={colHeaderClass}>Amount</div>
                      </div>
                      {balanceData.expenses?.details?.map(
                        (item: any, idx: number) => (
                          <div className="flex justify-between py-1" key={idx}>
                            <div className={cellClass}>{item.name}</div>
                            <div className={cellClass}>
                              {formatAfaAmount(item.balance)}
                            </div>
                          </div>
                        )
                      )}
                      <div className={totalClass}>
                        <span>Total Expenses</span>
                        <span>
                          {formatAfaAmount(balanceData.expenses?.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={`flex justify-between bg-positive mt-4 py-2`}>
                      <div className={`${colHeaderClass} text-white`}>
                        Net Income
                      </div>
                      <div className={`${colHeaderClass} text-white`}>
                        {formatAfaAmount(balanceData?.net_income)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Bottom Bar */}
            <div className="flex justify-around w-full px-4 items-start gap-8 py-4">
              <div className="w-1/2">
                <div className="bg-p_primary flex justify-between text-white text-title1 rounded px-3 py-3">
                  <span>Total Assets</span>
                  <span className="ml-4 text-heading6-semi">
                    {formatAfaAmount(balanceData?.assets?.total)}
                  </span>
                </div>
              </div>
              <div className="w-1/2 flex flex-col gap-1">
                <div className="bg-p_primary flex justify-between text-white text-title1 rounded px-3 py-3">
                  Total Amount
                  <span className="ml-4 text-heading6-semi">
                    {formatAfaAmount(balanceData?.total_amount)}
                  </span>
                </div>
                <h1 className=" text-body2 truncate">
                  Note : Total Amount = Liabilities + Equity or Capital + Revenue -
                  Expense
                </h1>
              </div>
            </div>

            {/* Empty state */}
            {!loading &&
              (!balanceData.assets ||
                !balanceData.assets.details ||
                balanceData.assets.details.length === 0) && (
                <div className="flex items-center justify-center h-emptyscreenheight absolute inset-0 bg-white bg-opacity-80 z-30">
                  <EmptyListingScreen emptyScreenText={filersWithEmpty} />
                </div>
              )}
          </>
        )}
      </div>
    );
}
